import CryptoJS from 'crypto-js';

export interface GravatarProfile {
  id: string;
  hash: string;
  requestHash: string;
  profileUrl: string;
  preferredUsername: string;
  thumbnailUrl: string;
  photos: Array<{
    value: string;
    type: string;
  }>;
  name: {
    givenName: string;
    familyName: string;
    formatted: string;
  };
  displayName: string;
  aboutMe: string;
  currentLocation: string;
  emails: Array<{
    primary: string;
    value: string;
  }>;
}

export class GravatarService {
  private static readonly BASE_URL = 'https://gravatar.com';
  private static readonly API_BASE_URL = 'https://api.gravatar.com/v3';
  
  static getEmailHash(email: string): string {
    return CryptoJS.MD5(email.toLowerCase().trim()).toString();
  }
  
  static getAvatarUrl(email: string, size: number = 200, defaultImage: string = 'identicon'): string {
    const hash = this.getEmailHash(email);
    return `${this.BASE_URL}/avatar/${hash}?s=${size}&d=${defaultImage}`;
  }
  
  static async getProfile(email: string): Promise<GravatarProfile | null> {
    const apiKey = import.meta.env.VITE_GRAVATAR_API_KEY;
    if (!apiKey) {
      return null;
    }
    
    try {
      const hash = this.getEmailHash(email);
      const response = await fetch(`${this.API_BASE_URL}/profiles/${hash}`, {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          // User doesn't have a Gravatar profile
          return null;
        }
        throw new Error(`Gravatar API error: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to fetch Gravatar profile:', error);
      return null;
    }
  }
  
  static async getHighQualityAvatar(email: string, size: number = 200): Promise<string> {
    const profile = await this.getProfile(email);
    
    if (profile && profile.photos && profile.photos.length > 0) {
      // Return the highest quality photo available
      const photo = profile.photos.find(p => p.type === 'thumbnail') || profile.photos[0];
      return photo.value;
    }
    
    // Fallback to standard avatar URL
    return this.getAvatarUrl(email, size);
  }
}